/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { EuiFlexGroup, EuiFlexItem, EuiTitle } from "@inscopix/ideas-eui";
import { useDataTableContext } from "../store/DataTableProvider";
import { useState } from "react";
import { ModalEditDataTable } from "../modals/ModalEditDataTable";
import { ModalEditAnalysisTable } from "../modals/ModalEditAnalysisTable";
import { DataTableButtonUpgrade } from "./DataTableButtonUpgrade";
import { Helmet } from "react-helmet-async";
import { DataTableToolInfo } from "./DataTableToolInfo";
import { DataTableComputeCredits } from "./DataTableComputeCredits";
import { ButtonIconPermissioned } from "components/ButtonIconPermissioned/ButtonIconPermissioned";

export interface DataTableHeaderProps {
  tableKey: string;
}
/**
 * Component that renders information and actions for a data table or analysis
 * table
 */
export const DataTableHeader = ({ tableKey }: DataTableHeaderProps) => {
  const [visibleModal, setVisibleModal] = useState<"data" | "analysis">();
  const projectName = useDataTableContext((s) => s.project.name);
  const selectedTable = useDataTableContext((s) => {
    return s.tables.find((table) => table.key === tableKey);
  });

  if (selectedTable === undefined) {
    return (
      <EuiTitle
        size="m"
        css={css`
          padding: 5px;
        `}
      >
        <h1>No table selected</h1>
      </EuiTitle>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${projectName}: ${selectedTable.name}`}</title>
      </Helmet>
      {visibleModal === "data" && (
        <ModalEditDataTable
          table={selectedTable}
          onClose={() => setVisibleModal(undefined)}
        />
      )}
      {visibleModal === "analysis" && (
        <ModalEditAnalysisTable
          table={selectedTable}
          onClose={() => setVisibleModal(undefined)}
        />
      )}
      <EuiFlexGroup
        alignItems="center"
        gutterSize="s"
        responsive={true}
        style={{ marginTop: "5px" }}
        justifyContent="spaceBetween"
      >
        <EuiFlexItem grow={false}>
          <EuiFlexGroup responsive={false} alignItems="center" gutterSize="s">
            <EuiFlexItem grow={false}>
              <EuiTitle size="s">
                <h1>
                  {selectedTable.name}{" "}
                  <span
                    css={css`
                      color: grey;
                    `}
                  >
                    ({selectedTable.key})
                  </span>
                </h1>
              </EuiTitle>
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              <ButtonIconPermissioned
                requiredPermission="edit"
                aria-label="Edit table"
                iconType="gear"
                onClick={() => setVisibleModal(selectedTable.kind)}
                style={{ marginTop: "2px" }}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
        {selectedTable.kind === "analysis" && (
          <EuiFlexItem grow={false}>
            <EuiFlexGroup responsive={false} alignItems="center" gutterSize="s">
              <EuiFlexItem grow={false}>
                <DataTableComputeCredits selectedTable={selectedTable} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <DataTableToolInfo selectedTable={selectedTable} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <DataTableButtonUpgrade tableId={selectedTable.id} />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </>
  );
};
